import {
  elementLoadTarget,
  nodeLoadTarget,
  type FrameLabArchiveView,
  type FrameLabElement,
  type FrameLabLoad,
  type FrameLabNode,
  type FrameLabSupport
} from './frameLabDocument'

export interface DrawingTransform {
  width: number
  height: number
  offsetX: number
  offsetY: number
  scale: number
}

export function ProjectCanvas({ archive }: { archive: FrameLabArchiveView }) {
  const drawing = makeDrawing(archive.project.nodes)
  const nodeByID = new Map(archive.project.nodes.map((node) => [node.id, node]))

  return (
    <div className="canvas-wrap">
      <svg className="project-svg" viewBox={`0 0 ${drawing.width} ${drawing.height}`} role="img" aria-label={`Schema ${archive.project.title}`}>
        <g transform={`translate(${drawing.offsetX} ${drawing.offsetY}) scale(${drawing.scale})`}>
          {archive.project.elements.map((element) => (
            <ElementLine key={element.id} element={element} nodeByID={nodeByID} />
          ))}
          {archive.project.supports.map((support) => (
            <SupportSymbol key={support.id} support={support} nodeByID={nodeByID} />
          ))}
          {archive.project.loads.filter((load) => load.isEnabled !== false).map((load) => (
            <LoadSymbol key={load.id} load={load} nodeByID={nodeByID} elements={archive.project.elements} />
          ))}
          {archive.project.nodes.map((node) => (
            <NodeSymbol key={node.id} node={node} />
          ))}
        </g>
      </svg>
    </div>
  )
}

export function ElementLine({ element, nodeByID }: { element: FrameLabElement; nodeByID: Map<string, FrameLabNode> }) {
  const start = nodeByID.get(element.startNodeID)
  const end = nodeByID.get(element.endNodeID)
  if (!start || !end) {
    return null
  }
  const midX = (start.position.x + end.position.x) / 2
  const midY = (start.position.y + end.position.y) / 2
  return (
    <g className={`element element-${element.type}`}>
      <line x1={start.position.x} y1={start.position.y} x2={end.position.x} y2={end.position.y} />
      <text x={midX} y={midY - 14}>{element.label}</text>
    </g>
  )
}

export function NodeSymbol({ node }: { node: FrameLabNode }) {
  return (
    <g className="node">
      <circle cx={node.position.x} cy={node.position.y} r="6" />
      <text x={node.position.x} y={node.position.y - 12}>{node.label}</text>
    </g>
  )
}

export function SupportSymbol({ support, nodeByID }: { support: FrameLabSupport; nodeByID: Map<string, FrameLabNode> }) {
  if (support.isEnabledInStatics === false) {
    return null
  }
  const node = nodeByID.get(support.nodeID)
  if (!node) {
    return null
  }
  const { x, y } = node.position
  if (support.type === 'fixed') {
    return <rect className="support" x={x - 18} y={y + 9} width="36" height="9" />
  }
  return (
    <g className={`support support-${support.type}`}>
      <path d={`M ${x} ${y + 8} L ${x - 15} ${y + 28} L ${x + 15} ${y + 28} Z`} />
      {support.type === 'roller' && (
        <>
          <circle cx={x - 8} cy={y + 34} r="3.5" />
          <circle cx={x + 8} cy={y + 34} r="3.5" />
        </>
      )}
    </g>
  )
}

export function LoadSymbol({
  load,
  nodeByID,
  elements
}: {
  load: FrameLabLoad
  nodeByID: Map<string, FrameLabNode>
  elements: FrameLabElement[]
}) {
  if (load.type === 'moment') {
    const nodeID = nodeLoadTarget(load)
    const node = nodeID ? nodeByID.get(nodeID) : undefined
    return node ? <MomentSymbol load={load} x={node.position.x} y={node.position.y} /> : null
  }
  if (load.type === 'distributedUniform') {
    return <DistributedLoad load={load} nodeByID={nodeByID} elements={elements} />
  }
  const nodeID = nodeLoadTarget(load)
  const node = nodeID ? nodeByID.get(nodeID) : undefined
  return node ? <PointForce load={load} x={node.position.x} y={node.position.y} /> : null
}

export function ArrowMarker() {
  return (
    <defs>
      <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" />
      </marker>
    </defs>
  )
}

export function makeDrawing(nodes: FrameLabNode[]): DrawingTransform {
  if (nodes.length === 0) {
    return { width: 900, height: 520, offsetX: 450, offsetY: 260, scale: 1 }
  }
  const xs = nodes.map((node) => node.position.x)
  const ys = nodes.map((node) => node.position.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const contentWidth = Math.max(1, maxX - minX)
  const contentHeight = Math.max(1, maxY - minY)
  const width = 900
  const height = 520
  const padding = 120
  const scale = Math.min((width - padding * 2) / contentWidth, (height - padding * 2) / contentHeight, 2.1)
  return {
    width,
    height,
    scale,
    offsetX: (width - contentWidth * scale) / 2 - minX * scale,
    offsetY: (height - contentHeight * scale) / 2 - minY * scale
  }
}

export function degreesToRadians(degrees: number): number {
  return degrees * Math.PI / 180
}

function PointForce({ load, x, y }: { load: FrameLabLoad; x: number; y: number }) {
  const angle = degreesToRadians(load.angleDegrees)
  const length = 42
  const dx = Math.cos(angle) * length
  const dy = Math.sin(angle) * length
  return (
    <g className="load">
      <line x1={x - dx} y1={y - dy} x2={x} y2={y} markerEnd="url(#arrow)" />
      <text x={x - dx * 1.12} y={y - dy * 1.12}>{load.label}</text>
      <ArrowMarker />
    </g>
  )
}

function MomentSymbol({ load, x, y }: { load: FrameLabLoad; x: number; y: number }) {
  return (
    <g className="load">
      <path d={`M ${x - 20} ${y} A 20 20 0 1 1 ${x + 5} ${y + 19}`} markerEnd="url(#arrow)" />
      <text x={x + 20} y={y - 18}>{load.label}</text>
      <ArrowMarker />
    </g>
  )
}

function DistributedLoad({
  load,
  nodeByID,
  elements
}: {
  load: FrameLabLoad
  nodeByID: Map<string, FrameLabNode>
  elements: FrameLabElement[]
}) {
  const target = elementLoadTarget(load)
  const element = target ? elements.find((candidate) => candidate.id === target.elementID) : undefined
  const start = element ? nodeByID.get(element.startNodeID) : undefined
  const end = element ? nodeByID.get(element.endNodeID) : undefined
  if (!start || !end) {
    return null
  }
  const count = 5
  const angle = degreesToRadians(load.angleDegrees)
  const dx = Math.cos(angle) * 28
  const dy = Math.sin(angle) * 28
  return (
    <g className="load distributed">
      {Array.from({ length: count }).map((_, index) => {
        const t = (index + 1) / (count + 1)
        const x = start.position.x + (end.position.x - start.position.x) * t
        const y = start.position.y + (end.position.y - start.position.y) * t
        return <line key={index} x1={x - dx} y1={y - dy} x2={x} y2={y} markerEnd="url(#arrow)" />
      })}
      <text x={(start.position.x + end.position.x) / 2} y={(start.position.y + end.position.y) / 2 - 34}>{load.label}</text>
      <ArrowMarker />
    </g>
  )
}
